import { useMemo } from "react"
import { ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts"
import { cn } from "@/lib/utils"

export interface SessionDataPoint {
  session: number
  attended: 0 | 1
  label?: string
  date?: string
  count?: number
  target?: number
  contacts?: number
  interviews?: number
  painPoints?: number
  cohort_avg?: number
}

interface AttendanceGraphCardProps {
  title: string
  data: SessionDataPoint[]
  color?: string
  className?: string
}

const colorMap: Record<string, { fill: string; stroke: string; bg: string; text: string }> = {
  blue: { fill: "#3b82f6", stroke: "#2563eb", bg: "bg-blue-50 dark:bg-blue-950/30", text: "text-blue-600 dark:text-blue-400" },
  amber: { fill: "#f59e0b", stroke: "#d97706", bg: "bg-amber-50 dark:bg-amber-950/30", text: "text-amber-600 dark:text-amber-400" },
  green: { fill: "#22c55e", stroke: "#16a34a", bg: "bg-green-50 dark:bg-green-950/30", text: "text-green-600 dark:text-green-400" },
  purple: { fill: "#a855f7", stroke: "#9333ea", bg: "bg-purple-50 dark:bg-purple-950/30", text: "text-purple-600 dark:text-purple-400" },
}

export function AttendanceGraphCard({
  title,
  data,
  color = "blue",
  className,
}: AttendanceGraphCardProps) {
  const colors = colorMap[color] || colorMap.blue

  const hasCohortAvg = data.some((d) => d.cohort_avg !== undefined && d.cohort_avg !== null)

  const cumulativeData = useMemo(() => {
    let cumulative = 0
    let cohortCumulative = 0
    return data.map((point) => {
      cumulative += point.attended
      if (point.cohort_avg !== undefined) {
        cohortCumulative += point.cohort_avg / 100
      }
      return { ...point, cumulative, cohortCumulative: hasCohortAvg ? Math.round(cohortCumulative * 10) / 10 : undefined }
    })
  }, [data, hasCohortAvg])

  const completed = data.filter((d) => d.attended === 1).length
  const total = data.length
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0

  if (total === 0) {
    return (
      <div className={cn("bg-card border border-border rounded-lg p-4", className)}>
        <p className="text-sm text-muted-foreground font-medium mb-1">{title}</p>
        <div className="flex items-center justify-center h-[120px]">
          <p className="text-sm text-muted-foreground">No data yet</p>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("bg-card border border-border rounded-lg p-4", className)}>
      <p className="text-sm text-muted-foreground font-medium mb-1">{title}</p>
      <p className={cn("text-xl font-bold", colors.text)}>
        {completed}/{total}{" "}
        <span className="text-sm font-normal text-muted-foreground">{percentage}%</span>
      </p>
      <div className="mt-2" style={{ height: 120 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={cumulativeData} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
            <defs>
              <linearGradient id={`gradient-${color}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors.fill} stopOpacity={0.3} />
                <stop offset="95%" stopColor={colors.fill} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <XAxis dataKey="session" hide />
            <YAxis hide domain={[0, "dataMax"]} />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null
                const d = payload[0].payload
                return (
                  <div className="bg-popover border border-border rounded px-2 py-1 shadow-sm">
                    <p className="text-xs font-medium">{d.label || `Session ${d.session}`}</p>
                    {d.date && <p className="text-xs text-muted-foreground">{d.date}</p>}
                    <p className="text-xs text-muted-foreground">
                      {d.count !== undefined ? (
                        <>
                          {d.count} completed
                          {d.target !== undefined && <> (target: {d.target})</>}
                          {d.contacts !== undefined && (
                            <><br />{d.contacts} contacts, {d.interviews} interviews, {d.painPoints} pain points</>
                          )}
                        </>
                      ) : (
                        d.attended === 1 ? "Done" : "Missed"
                      )}
                    </p>
                    {d.cohort_avg !== undefined && (
                      <p className="text-xs text-slate-400">Cohort avg: {d.cohort_avg}%</p>
                    )}
                  </div>
                )
              }}
            />
            <Area
              type="monotone"
              dataKey="cumulative"
              stroke={colors.stroke}
              strokeWidth={2}
              fill={`url(#gradient-${color})`}
              dot={false}
              activeDot={{ r: 3, strokeWidth: 0, fill: colors.stroke }}
            />
            {hasCohortAvg && (
              <Line
                type="monotone"
                dataKey="cohortCumulative"
                stroke="#94a3b8"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={false}
                activeDot={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
